import { MatDialog } from '@angular/material/dialog';
import { MAT_DIALOG_CUSTOM_CLASS } from '@modeso/aumico-lib-core-fe';
import {
    AccountMappingAccountItem,
    AccountMappingTemplateGroupItem,
    IAccount,
    IAccountMappingSubFLI,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    ItemType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { filter, take } from 'rxjs';
import { SwitchAccountsDialogComponent } from '../components/account-mapping/switch-accounts-form/switch-accounts-form.dialog';
import { IGroupAccountMappingComponentDelegate } from '../interfaces/IGroupAccountMappingComponentDelegate.interface';
import { Item, Parent } from '../interfaces/menu-item.class';
import { ISwitchAccountsData } from '../interfaces/switch-accounts-data.interface';
import { isFli, isGroup } from '../type-guards/type-guards';

export class MenuItemActionExecutionHelper {
    constructor(private delegate: IGroupAccountMappingComponentDelegate, private readonly dialog: MatDialog) {}

    public addMappingItem(
        item: Item,
        parent: Parent,
        mappingItemType: ItemType.HEADER | ItemType.EMPTY,
        direction: 'ABOVE' | 'BELOW',
    ): Item {
        if (parent == null) {
            return null;
        }

        if (isFli(parent)) {
            return this.handleFliParent(mappingItemType, item as AccountMappingAccountItem, parent, direction);
        } else if (isGroup(parent)) {
            /* Parent is group check if the clicked item is fli/subtotal or subgroup */
            return this.handleGroupParent(
                mappingItemType,
                parent,
                item as IAccountMappingTemplateGroup | AccountMappingTemplateGroupItem,
                direction,
            );
        }
        return null;
    }

    public splitAccount(
        account: IAccount,
        parentItem: IAccountMappingSubFLI | IAccountMappingTemplateGroupItemFLI,
        accountIndex: number,
    ): void {
        const accountTopGroupName = this.delegate.templateGroup.name;
        const switchAccountOptions = this.delegate.getFlisAndSubFlisForSwitchAccount(accountTopGroupName);

        const splittedAccountValues = this.delegate.getSplitAccountValues(
            account.calculatedClosingBalance,
            account.calculatedOpeningBalance,
            accountTopGroupName,
        );
        const dialogRef = this.dialog.open(SwitchAccountsDialogComponent, {
            width: '50%',
            data: {
                accountNumber: account.accountNumber,
                accountName: account.name,
                currentValue: account.calculatedClosingBalance,
                previousValue: account.calculatedOpeningBalance,
                parentOptions: switchAccountOptions,
                splitAccountValues: splittedAccountValues,
            } as ISwitchAccountsData,
            panelClass: MAT_DIALOG_CUSTOM_CLASS,
        });

        dialogRef
            .afterClosed()
            .pipe(
                filter((result) => result != undefined),
                take(1),
            )
            .subscribe((result: IAccountMappingSubFLI | IAccountMappingTemplateGroupItemFLI) => {
                this.delegate.splitAccount(account, parentItem, accountIndex, accountTopGroupName, result);
            });
    }

    private getItemIndexInsideFliArrayOfItems(
        item: AccountMappingAccountItem,
        parent: IAccountMappingTemplateGroupItemFLI,
        direction: 'ABOVE' | 'BELOW',
    ): number {
        let itemIndex;
        if (Object.values<string>(ItemType).includes(item.type)) {
            itemIndex = this.delegate.getSubfliOrOfThatItemIndexInsideFli(item, parent);
        } else {
            const account = item as IAccount;
            itemIndex = this.delegate.getAccountIndexInsideFli(account, parent);
        }

        return direction === 'ABOVE' ? itemIndex : itemIndex + 1;
    }

    private getItemIndexInsideGroupItems(
        item: AccountMappingTemplateGroupItem,
        parent: IAccountMappingTemplateGroup,
        direction: 'ABOVE' | 'BELOW',
    ): number {
        const itemIndex = this.delegate.getItemIndexInsideGroup(item, parent);
        return direction === 'ABOVE' ? itemIndex : itemIndex + 1;
    }

    private getSubGroupIndexInsideGroup(
        item: IAccountMappingTemplateGroup,
        parent: IAccountMappingTemplateGroup,
        direction: 'ABOVE' | 'BELOW',
    ): number {
        const itemIndex = this.delegate.getSubGroupIndexInsideGroup(item, parent);
        return direction === 'ABOVE' ? itemIndex : itemIndex + 1;
    }

    private getItemIndexInsideSortedItems(
        item: IAccountMappingTemplateGroup | AccountMappingTemplateGroupItem,
        parent: IAccountMappingTemplateGroup,
        direction: 'ABOVE' | 'BELOW',
    ): number {
        const itemIndexInSortedItems = this.delegate.getItemIndexInsideSortedItems(item, parent);
        return direction === 'ABOVE' ? itemIndexInSortedItems : itemIndexInSortedItems + 1;
    }

    private handleFliParent(
        mappingItemType: ItemType,
        item: AccountMappingAccountItem,
        parent: Parent,
        direction: 'ABOVE' | 'BELOW',
    ): AccountMappingAccountItem {
        if (!isFli(parent)) return null;
        let mappingItem: AccountMappingAccountItem;
        if (mappingItemType === ItemType.HEADER) {
            mappingItem = {
                name: `${item.name}_${new Date().getTime()}`,
                type: ItemType.HEADER,
                caption: undefined,
            };
        } else {
            mappingItem = {
                name: `${item.name}_${new Date().getTime()}`,
                type: ItemType.EMPTY,
            };
        }
        const clickedItemIndex = this.getItemIndexInsideFliArrayOfItems(item, parent, direction);
        this.delegate.insertItemInFli(mappingItem, clickedItemIndex, parent);

        return mappingItem;
    }

    private handleGroupParent(
        mappingItemType: ItemType.HEADER | ItemType.EMPTY,
        parent: IAccountMappingTemplateGroup,
        item: IAccountMappingTemplateGroup | AccountMappingTemplateGroupItem,
        direction: 'ABOVE' | 'BELOW',
    ): AccountMappingTemplateGroupItem | IAccountMappingTemplateGroup {
        const siblingAsTemplateGroupItem = item as AccountMappingTemplateGroupItem;
        let addedItem: AccountMappingTemplateGroupItem | IAccountMappingTemplateGroup;
        if (Object.values(ItemType).includes(siblingAsTemplateGroupItem.type)) {
            /* insert item inside the items array */
            addedItem = this.handleInsideItemsOfGroup(
                mappingItemType,
                parent,
                item as AccountMappingTemplateGroupItem,
                direction,
            );
        } else {
            addedItem = this.handleInsideGroupsOfGroup(
                mappingItemType,
                parent,
                item as IAccountMappingTemplateGroup,
                direction,
            );
        }

        return addedItem;
    }

    private handleInsideItemsOfGroup(
        mappingItemType: ItemType.HEADER | ItemType.EMPTY,
        parent: IAccountMappingTemplateGroup,
        item: AccountMappingTemplateGroupItem,
        direction: 'ABOVE' | 'BELOW',
    ): AccountMappingTemplateGroupItem {
        const mappingItem: AccountMappingTemplateGroupItem = {
            name: `${item.name}_${new Date().getTime()}`,
            caption: undefined,
            type: mappingItemType,
        };
        const clickedItemIndex = this.getItemIndexInsideGroupItems(item, parent, direction);
        const clickedItemIndexInSortedItems = this.getItemIndexInsideSortedItems(item, parent, direction);

        this.delegate.insertItemInItemsOfGroup(mappingItem, clickedItemIndex, parent);
        this.delegate.insertItemInSortedItems(mappingItem, clickedItemIndexInSortedItems, parent);

        return mappingItem;
    }

    private handleInsideGroupsOfGroup(
        mappingItemType: ItemType,
        parent: IAccountMappingTemplateGroup,
        item: IAccountMappingTemplateGroup,
        direction: 'ABOVE' | 'BELOW',
    ): IAccountMappingTemplateGroup {
        const mappingItem = {
            name: `${item.name}_${new Date().getTime()}`,
            isEmpty: mappingItemType === ItemType.EMPTY,
            isHeader: mappingItemType === ItemType.HEADER,
            groups: [],
            items: [],
            sortedItems: [],
        } as IAccountMappingTemplateGroup;

        const clickedItemIndexInGroups = this.getSubGroupIndexInsideGroup(item, parent, direction);
        const clickedItemIndexInSortedItems = this.getItemIndexInsideSortedItems(item, parent, direction);

        this.delegate.insertItemInGroupsOfGroup(mappingItem, clickedItemIndexInGroups, parent);
        this.delegate.insertItemInSortedItems(mappingItem, clickedItemIndexInSortedItems, parent);

        return mappingItem;
    }
}
